<script setup>
import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import QRCode from 'qrcodejs2'
import getUA from '@utils/getUA'
import todayAxios from '@utils/global/todayAxiosGlobal'

const route = useRoute()
const isPC = ref(window.innerWidth >= 768)

const appStoreURL =
  'https://apps.apple.com/app/apple-store/id6446071834?pt=126093046&ct=officialsite&mt=8'

const apkURL =
  'https://play.google.com/store/apps/details?id=com.oceangalaxy.camera.new'

// Google Play 的 install referrer 只接受一个 referrer 参数，
// 无自定义 referrer 时用这个默认值兜底
const DEFAULT_ANDROID_REFERRER = 'referrer=utm_source%3Dofficialsite'

const WEB_FALLBACK_URLS = {
  teamspace: 'https://teamspace.timemark.com/',
  overview: 'https://landing.timemark.com/timemark-overview',
  home: 'https://www.timemark.com'
}

const UTM_KEYS = [
  'utm_source',
  'utm_campaign',
  'utm_medium',
  'utm_content',
  'utm_term',
  'utm_campaign_id'
]

// 带 web_fallback 时本页只是中转页，不渲染任何文案，只显示 loading
const isWebFallback = ref(!!WEB_FALLBACK_URLS[route.query.web_fallback])

onMounted(() => {
  if (isPC.value) {
    if (!redirectToWebFallback()) {
      renderQRCode()
    }
  } else {
    init()
  }
})

// PC 端若带有 web_fallback 参数，则跳转到对应的 web 页面
// （携带当前 URL 的参数，但剔除 web_fallback 和 target）
function redirectToWebFallback() {
  const baseURL = WEB_FALLBACK_URLS[route.query.web_fallback]
  if (!baseURL) return false

  const searchParams = new URLSearchParams(window.location.search)
  searchParams.delete('web_fallback')
  searchParams.delete('target')

  // referrer 里携带的是编码后的 utm 串，展开成独立的 utm_xxx 参数
  // 而不是把整个 referrer 透传过去
  const referrer = searchParams.get('referrer')
  if (referrer) {
    searchParams.delete('referrer')
    const utmParams = new URLSearchParams(decodeURIComponent(referrer))
    UTM_KEYS.forEach((key) => {
      const value = utmParams.get(key)
      if (value) searchParams.set(key, value)
    })
  }

  const params = searchParams.toString()

  window.location.href = params
    ? `${baseURL}${baseURL.includes('?') ? '&' : '?'}${params}`
    : baseURL
  return true
}

function renderQRCode() {
  new QRCode(document.getElementById('qrcode'), {
    text: window.location.href,
    width: 200,
    height: 200,
    colorDark: '#000000',
    colorLight: '#ffffff',
    correctLevel: QRCode.CorrectLevel.L
  })
}

function collectFingerprint() {
  const userAgent = navigator.userAgent
  const screenResolution = `${screen.width}x${screen.height}`
  const pixelRatio = window.devicePixelRatio
  const timezone = -new Date().getTimezoneOffset()
  const language = navigator.language
  const timestamp = Date.now()

  const deviceModelMatch = userAgent.match(/\((.*?)\)/)
  const deviceInfo = deviceModelMatch ? deviceModelMatch[1] : ''
  const deviceModel = deviceInfo.split(';')[0]?.trim() || ''
  const systemVersion = deviceInfo.split(';')[1]?.trim() || ''

  const [screenWidth, screenHeight] = screenResolution.split('x').map(Number)

  return {
    user_agent: userAgent,
    screen_resolution: screenResolution,
    pixel_ratio: pixelRatio,
    timezone: timezone.toString(),
    language: language,
    timestamp: timestamp,
    device_model: deviceModel,
    system_version: systemVersion,
    screen_width: screenWidth,
    screen_height: screenHeight,
    scale: pixelRatio,
    language_code: language.split('-')[0]
  }
}

async function parseAndSetUtmParams(referrer) {
  if (!referrer) return

  try {
    const decodedReferrer = decodeURIComponent(referrer)
    const urlParams = new URLSearchParams(decodedReferrer)
    const utmParams = {}

    UTM_KEYS.forEach((key) => {
      const value = urlParams.get(key)
      if (value) utmParams[key] = value
    })

    if (Object.keys(utmParams).length > 0) {
      const fingerprint = collectFingerprint()
      // 不同平台的 clickId 参数名不同：Facebook 为 fbclid，Google 为 gclid
      const clickId =
        route.query.clickId || route.query.fbclid || route.query.gclid
      await todayAxios.post('/next/app/user/web/attribution/save', {
        fingerprint: JSON.stringify(fingerprint),
        ...utmParams,
        ...(clickId ? { clickId } : {}),
        ...(route.query.target ? { target: route.query.target } : {})
      })
    }
  } catch (error) {
    console.error('解析 referrer 参数失败:', error)
  }
}

async function init() {
  const UA = getUA()
  let queryParams = ''

  if (UA.isIOS && route.query.ct) {
    queryParams = `ct=${route.query.ct}&pt=126093046&mt=8`
  }

  if (UA.isIOS && route.query.referrer) {
    await parseAndSetUtmParams(route.query.referrer)
  }

  if (UA.isAndroid) {
    if (route.query.referrer) {
      // referrer 携带 utmParams，若同时带有 target，则把 target 追加到 referrer 末尾：
      // <referrer>&target=<target>
      const referrer = route.query.target
        ? `${route.query.referrer}&target=${route.query.target}`
        : route.query.referrer
      queryParams = `referrer=${encodeURIComponent(
        encodeURIComponent(referrer)
      )}`
    } else if (route.query.target) {
      // 没有 referrer 但带有 target 时（如 ?target=teamspace&web_fallback=teamspace），
      // 用 target 构造 referrer：referrer=target%3D<target>
      queryParams = `referrer=${encodeURIComponent(
        encodeURIComponent(`target=${route.query.target}`)
      )}`
    } else {
      // 既无 referrer 也无 target，使用默认 referrer
      queryParams = DEFAULT_ANDROID_REFERRER
    }
  }

  switch (true) {
    case UA.isIOS:
      window.location.href = `${appStoreURL}${
        queryParams ? '&' + queryParams : ''
      }`
      break
    case UA.isAndroid:
      window.location.href = `${apkURL}&${queryParams}`
      break
    default:
      window.location.href = 'https://timemark.com/'
      break
  }
}
</script>

<template>
  <div class="redirect-loading" v-if="isWebFallback">
    <span class="spinner"></span>
  </div>
  <div class="pc-container" v-else-if="isPC">
    <header class="header">
      <a href="https://www.timemark.com" class="header-brand">
        <img src="../assets/logo.png" class="logo" />
        <span class="name">Timemark</span>
      </a>
    </header>
    <section class="hero">
      <div class="hero-content">
        <div class="text-group">
          <h1 class="title">Download the app to unlock the full experience</h1>
          <p class="desc">
            Timemark is built for the field. The mobile app stamps every photo
            with verified time and GPS the moment you capture it. Download the
            app if you haven't yet. It's
            <span style="font-weight: bold">the only way</span> to capture
            photos.
          </p>
        </div>
        <div class="qr-group">
          <div id="qrcode" class="qrcode"></div>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped lang="scss">
* {
  box-sizing: border-box;
}

.redirect-loading {
  min-height: 100vh;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;

  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e5e5e5;
    border-top-color: #000;
    border-radius: 50%;
    animation: redirect-spin 0.8s linear infinite;
  }
}

@keyframes redirect-spin {
  to {
    transform: rotate(360deg);
  }
}

.pc-container {
  min-height: 100vh;
  background: #ffffff;
  display: flex;
  flex-direction: column;

  .header {
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;

    .header-brand {
      display: flex;
      align-items: center;
      text-decoration: none;

      .logo {
        width: 40px;
        height: 40px;
        border-radius: 6px;
        margin-right: 10px;
      }

      .name {
        font-size: 18px;
        font-weight: 700;
        color: #000;
        font-family: 'Plus Jakarta Sans', sans-serif;
      }
    }
  }

  .hero {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 20px;

    .hero-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 48px;

      .text-group {
        max-width: 720px;

        .title {
          font-size: 50px;
          font-weight: 800;
          color: #000;
          line-height: 1.2;
          margin: 0 0 24px 0;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }

        .desc {
          font-size: 18px;
          font-weight: 400;
          color: #000;
          line-height: 1.6;
          margin: 0;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }
      }

      .qr-group {
        .qrcode {
          padding: 16px;
          background: #fff;
        }
      }
    }
  }
}
</style>
